import { useState, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { matchSorter } from 'match-sorter';

import { sortKeyProfile, sortKeyGroup } from '@/lib/menu/RightMenu/matchSorterKey';

//components
import Input from '@/components/base/Input';
import Icon from '@/components/base/Icon';
import { AvatarMenu } from '@/components/composite/SocialProfiles/Components/AvatarMenu';
import { ListWrapper } from '@/components/composite/SocialProfiles/Components/ListWrapper';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import type { DefaultAvatarProps } from '@/components/base/Avatar';

type ListItem = DefaultAvatarProps & {
    data: SocialProfileType | SpGroupType;
    onClick: () => void;
};

function ListItem({ data, onClick, ...restProps }: ListItem) {
    return (
        <li>
            <button className="w-full" onClick={onClick} data-testid="list-item-button">
                <AvatarMenu
                    hasHover={false}
                    variant={'name' in data ? 'group' : 'social-profile'}
                    data={data}
                    compact={false}
                    {...restProps}
                />
            </button>
        </li>
    );
}

type SideBarListProps = {
    activeItem?: SocialProfileType | SpGroupType;
    data: (SocialProfileType | SpGroupType)[];
    variant: 'social-profile' | 'group';
    updateActiveItem: (item: SocialProfileType | SpGroupType) => void;
    toggleSideBar?: () => void;
};
export function SideBarList({
    activeItem,
    data,
    variant,
    updateActiveItem,
    toggleSideBar
}: SideBarListProps) {
    const { t } = useTranslation('commons');
    const inputSearchRef = useRef<HTMLInputElement>();
    const defaultValues = {
        active: activeItem ? [activeItem] : [],
        additional: data
    };

    const [itemsList, setItemsList] = useState(defaultValues);
    const filterLists = (searchValue: string) => {
        // Reset lists if the input is empty
        if (!searchValue) {
            setItemsList(defaultValues);
        }

        let otherItems: (SocialProfileType | SpGroupType)[] = [];
        let itemActive: (SocialProfileType | SpGroupType)[] = [];
        if (variant === 'social-profile') {
            // Filter on Additional social profiles
            otherItems = matchSorter(data as SocialProfileType[], searchValue, sortKeyProfile);

            // Filter on Already displayed social profiles
            itemActive = matchSorter(
                [activeItem] as SocialProfileType[],
                searchValue,
                sortKeyProfile
            );
        }
        if (variant === 'group') {
            // Filter on Additional social profiles
            otherItems = matchSorter(data as SpGroupType[], searchValue, sortKeyGroup);

            // Filter on Already displayed social profiles
            itemActive = matchSorter([activeItem] as SpGroupType[], searchValue, sortKeyGroup);
        }
        setItemsList({
            active: itemActive,
            additional: otherItems
        });
    };

    return (
        <ListWrapper data-testid="items-wrapper">
            <div className="flex items-center space-x-8 px-6">
                <Input
                    id="search-profile"
                    inputStyle="search"
                    noBorder={true}
                    ref={inputSearchRef}
                    onChange={(e) => filterLists(e.target.value)}
                    aria-label={
                        variant === 'social-profile'
                            ? t('add_social_profiles.search_social_profiles')
                            : t('add_social_profiles.search_groups')
                    }
                    placeholder={
                        variant === 'social-profile'
                            ? t('add_social_profiles.search_social_profiles')
                            : t('add_social_profiles.search_groups')
                    }
                    beforeInput={<Icon icon="search" />}
                />
            </div>
            <ul className="scrollbar max-h-[calc(100vh-180px)] flex-1 space-y-3.5 overflow-x-hidden px-6 pb-m pt-2">
                {itemsList.active.map((item) => {
                    return (
                        <ListItem
                            data-testid={`active-item-${
                                variant === 'group' ? 'group' : 'social-profile'
                            }`}
                            data={item}
                            key={item.id}
                            selected
                            onClick={() => {
                                if (typeof toggleSideBar === 'function') {
                                    toggleSideBar();
                                }
                                if (variant === 'group') {
                                    updateActiveItem(item);
                                }
                            }}
                        />
                    );
                })}
                {itemsList.additional?.map((item) => {
                    return (
                        <ListItem
                            data-testid={`additional-item-${
                                variant === 'group' ? 'group' : 'social-profile'
                            }`}
                            data={item}
                            key={item.id}
                            onClick={() => {
                                updateActiveItem(item);
                                if (typeof toggleSideBar === 'function') {
                                    toggleSideBar();
                                }
                            }}
                        />
                    );
                })}
            </ul>
        </ListWrapper>
    );
}
